import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import { Upload, FileText, Bell, FolderOpen, LogOut, User } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const features = [
  { icon: Upload, title: "Document Upload", desc: "Securely upload documents for review and analysis by your advisory team." },
  { icon: FileText, title: "Memoranda & Analysis", desc: "Access legal memoranda, strategic analyses, and advisory deliverables." },
  { icon: FolderOpen, title: "Matter Tracking", desc: "Track the status of advisory matters and engagement milestones." },
  { icon: Bell, title: "Regulatory Alerts", desc: "Receive regulatory alerts and policy updates relevant to your matters." },
];

const ClientPortal = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<{ display_name: string | null; company: string | null; approved: boolean | null } | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      supabase
        .from("profiles")
        .select("display_name, company, approved")
        .eq("user_id", user.id)
        .single()
        .then(({ data }) => {
          if (data) setProfile(data);
        });
    }
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out");
    navigate("/");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (!user) return null;

  if (profile && !profile.approved) {
    return (
      <Layout>
        <PageHead title="Client Portal" description="Secure client engagement portal." path="/client-portal" />
        <section className="section-padding">
          <div className="editorial-container max-w-md">
            <div className="bg-card rounded-lg p-10 shadow-elevated border border-border text-center">
              <h1 className="font-serif text-2xl font-semibold text-foreground mb-4">Access Pending</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Your account is pending approval. You will receive access once your engagement has been confirmed by Batts Legal LLP.
              </p>
              <button onClick={handleSignOut} className="text-sm text-primary hover:underline">Sign Out</button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHead title="Client Portal" description="Secure client engagement portal for document upload, legal memoranda, matter tracking, and regulatory alerts." path="/client-portal" />
      <section className="bg-hero text-primary-foreground py-20">
        <div className="editorial-container flex items-start justify-between">
          <div>
            <div className="gold-accent-line mb-6" />
            <h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">Client Portal</h1>
            <p className="text-lg opacity-80 max-w-2xl">
              Welcome back{profile?.display_name ? `, ${profile.display_name}` : ""}.
              {profile?.company ? ` — ${profile.company}` : ""}
            </p>
          </div>
          <button onClick={handleSignOut} className="flex items-center gap-2 bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground px-4 py-2 rounded-sm text-sm transition-colors mt-2">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </section>

      <section className="section-padding">
        <div className="editorial-container max-w-3xl">
          <div className="bg-card rounded-lg p-6 shadow-card border border-border mb-8 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">{profile?.display_name || user.email}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-16">
            {features.map((f) => (
              <div key={f.title} className="bg-card rounded-md p-8 shadow-card border border-border">
                <f.icon className="w-8 h-8 text-gold mb-4" />
                <h3 className="font-serif text-lg font-semibold text-foreground mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground text-center">
            For portal support, contact your engagement partner or email info@battslegal.com.
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default ClientPortal;
